import React, { useState, useEffect, useRef } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Filter, X, CalendarDays, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ColumnFilterInput — renders the appropriate filter input based on column filterType
 */
const ColumnFilterInput = ({ column, value, onChange }) => {
  switch (column.filterType) {
    case 'text':
      return <TextFilter value={value || ''} onChange={onChange} />;
    case 'select':
      return <SelectFilter value={value || []} onChange={onChange} options={column.options || []} labels={column.optionLabels} />;
    case 'currency':
    case 'percent':
      return <RangeFilter value={value || {}} onChange={onChange} />;
    case 'date':
      return <DateFilter value={value || {}} onChange={onChange} />;
    default:
      return null;
  }
};

// --- Text filter with debounce ---
const TextFilter = ({ value, onChange }) => {
  const [local, setLocal] = useState(value);
  const timerRef = useRef(null);

  useEffect(() => { setLocal(value); }, [value]);

  const handleChange = (e) => {
    const v = e.target.value;
    setLocal(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onChange(v), 300);
  };

  return (
    <input
      type="text"
      value={local}
      onChange={handleChange}
      placeholder="Поиск..."
      className="w-full h-6 px-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:border-blue-400"
      onClick={(e) => e.stopPropagation()}
    />
  );
};

// --- Select filter (multi-select via popover) ---
const SelectFilter = ({ value, onChange, options, labels }) => {
  const selected = value || [];
  const hasSelection = selected.length > 0;

  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter(v => v !== opt)
      : [...selected, opt];
    onChange(next);
  };

  const getLabel = (opt) => (labels && labels[opt]) || opt;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1 h-6 px-1.5 text-xs border rounded w-full truncate",
            hasSelection
              ? "border-blue-400 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {hasSelection ? `${selected.length} выбр.` : 'Все'}
          </span>
          {hasSelection && (
            <X
              className="w-3 h-3 shrink-0 ml-auto hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {options.map(opt => (
            <button
              key={opt}
              className={cn(
                "flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-50",
                selected.includes(opt) && "bg-blue-50 text-blue-700"
              )}
              onClick={() => toggle(opt)}
            >
              <div className={cn(
                "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0",
                selected.includes(opt) ? "bg-blue-500 border-blue-500" : "border-gray-300"
              )}>
                {selected.includes(opt) && <Check className="w-2.5 h-2.5 text-white" />}
              </div>
              {getLabel(opt)}
            </button>
          ))}
        </div>
        {hasSelection && (
          <button
            className="w-full text-xs text-gray-400 hover:text-gray-600 mt-2 pt-2 border-t"
            onClick={() => onChange([])}
          >
            Сбросить
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

// --- Range filter (min/max for currency and percent) ---
const RangeFilter = ({ value, onChange }) => {
  return (
    <div className="flex items-center gap-0.5">
      <input
        type="number"
        value={value.min ?? ''}
        onChange={(e) => onChange({ ...value, min: e.target.value || null })}
        placeholder="от"
        className="w-1/2 h-6 px-1 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:border-blue-400"
        onClick={(e) => e.stopPropagation()}
      />
      <input
        type="number"
        value={value.max ?? ''}
        onChange={(e) => onChange({ ...value, max: e.target.value || null })}
        placeholder="до"
        className="w-1/2 h-6 px-1 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:border-blue-400"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// --- Date filter (from/to via calendar popover) ---
const DateFilter = ({ value, onChange }) => {
  const hasRange = value.from || value.to;

  const formatShort = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
  };

  const label = hasRange
    ? `${formatShort(value.from) || '...'} – ${formatShort(value.to) || '...'}`
    : 'Дата';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1 h-6 px-1.5 text-xs border rounded w-full truncate",
            hasRange
              ? "border-blue-400 bg-blue-50 text-blue-700"
              : "border-gray-200 bg-white text-gray-400 hover:border-gray-300"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <CalendarDays className="w-3 h-3 shrink-0" />
          <span className="truncate">{label}</span>
          {hasRange && (
            <X
              className="w-3 h-3 shrink-0 ml-auto hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); onChange({}); }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="space-y-3">
          <div>
            <div className="text-xs text-gray-500 mb-1">От:</div>
            <Calendar
              mode="single"
              selected={value.from ? new Date(value.from) : undefined}
              onSelect={(date) => onChange({ ...value, from: date ? date.toISOString().split('T')[0] : null })}
              className="rounded border"
            />
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">До:</div>
            <Calendar
              mode="single"
              selected={value.to ? new Date(value.to) : undefined}
              onSelect={(date) => onChange({ ...value, to: date ? date.toISOString().split('T')[0] : null })}
              className="rounded border"
            />
          </div>
          {hasRange && (
            <button
              className="w-full text-xs text-gray-400 hover:text-gray-600 pt-2 border-t"
              onClick={() => onChange({})}
            >
              Сбросить
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ColumnFilterInput;
